import type { HealthOverviewData } from '@/domain/health';
import type { HealthLastDayWelfare } from '@/domain/health/overviewTypes';

export interface HealthWoundsDescriptionData {
    noWounds: number;
    activeWounds: number;
    healingWounds: number;
    smallWound: number;
    mediumWound: number;
    bigWound: number;
}

export interface HealthWoundsInsightDisplayData {
    /** Latest-day `any_wound` prevalence (0–1), matching location overview `woundRate`. */
    totalWoundsRate: number | null;
    /** Weekly `any_wound` trend (0–1), matching location overview `woundSlope`. */
    totalWoundsSlope: number | null;
    acutePercent: number | null;
    healingPercent: number | null;
    woundDescription: HealthWoundsDescriptionData;
    noWoundPercent: number | null;
    oneWoundPercent: number | null;
    moreThanOneWoundPercent: number | null;
    fishCount: number | null;
    lastMeasurement: string;
    downGradeEstimatePercent: number | null;
}

/** API welfare shares are 0–1 fractions; display formatters expect 0–100 percentage points. */
function toDisplayPercent(value: number | null | undefined): number | null {
    if (value == null || Number.isNaN(value)) {
        return null;
    }
    return value * 100;
}

function sumFraction(...values: (number | null | undefined)[]): number | null {
    const defined = values.filter((v): v is number => v != null && !Number.isNaN(v));
    if (defined.length === 0) {
        return null;
    }
    return defined.reduce((sum, value) => sum + value, 0);
}

function mapWoundDescription(lastDay: HealthLastDayWelfare): HealthWoundsDescriptionData {
    return {
        noWounds: lastDay.noWound ?? 0,
        activeWounds: lastDay.anyAcuteWound ?? 0,
        healingWounds: lastDay.numHealingWounds ?? 0,
        smallWound: lastDay.smallWound ?? 0,
        mediumWound: lastDay.mediumWound ?? 0,
        bigWound: lastDay.bigWound ?? 0,
    };
}

export function mapHealthOverviewToWoundsInsightDisplay(
    overview: HealthOverviewData | undefined
): HealthWoundsInsightDisplayData | null {
    if (!overview) {
        return null;
    }

    const lastDay = overview.summary.lastDayWelfare;

    const totalWoundsRate =
        lastDay.anyWound != null && !Number.isNaN(lastDay.anyWound)
            ? lastDay.anyWound
            : sumFraction(lastDay.oneWound, lastDay.moreThanOneWound);

    return {
        totalWoundsRate,
        totalWoundsSlope: overview.summary.woundSlope,
        acutePercent: toDisplayPercent(lastDay.anyAcuteWound),
        healingPercent: toDisplayPercent(lastDay.numHealingWounds),
        woundDescription: mapWoundDescription(lastDay),
        noWoundPercent: toDisplayPercent(lastDay.noWound),
        oneWoundPercent: toDisplayPercent(lastDay.oneWound),
        moreThanOneWoundPercent: toDisplayPercent(lastDay.moreThanOneWound),
        fishCount: overview.summary.woundCount,
        lastMeasurement: overview.context.asOf,
        downGradeEstimatePercent: toDisplayPercent(lastDay.notSuperior),
    };
}
